import { AllTranscriptsProps } from '@/types';
import { HIDDEN_METADATA_KEYS, isRenderableMetadataValue } from '@/utils/metadataDisplay';

/**
 * The transcripts table renders one column per conversation.metadata key.
 * Two layers keep internal bookkeeping out of it:
 *  - the explicit HIDDEN_METADATA_KEYS set (string-valued-but-internal keys
 *    like `computed_quality`);
 *  - a STRUCTURAL rule (TELV1-506): any key seen with an OBJECT value in any
 *    conversation is dropped, so the translation pipeline's per-language
 *    bookkeeping (and any future object-valued key) is hidden automatically
 *    without ever having to extend the explicit set again.
 */
const getUniqueMetadataKeys = (allConversations: AllTranscriptsProps[]): Set<string> => {
  const metadataKeys = new Set<string>();
  const objectValuedKeys = new Set<string>();

  allConversations.forEach((item) => {
    if (!item.metadata) return;
    Object.entries(item.metadata).forEach(([key, value]) => {
      if (!isRenderableMetadataValue(value)) {
        objectValuedKeys.add(key);
        return;
      }
      if (!HIDDEN_METADATA_KEYS.has(key)) {
        metadataKeys.add(key);
      }
    });
  });

  // A key that is object-valued in ANY conversation is internal bookkeeping —
  // drop it everywhere, even if another conversation happened to carry a
  // primitive under the same key.
  objectValuedKeys.forEach((key) => metadataKeys.delete(key));

  return metadataKeys;
};

/**
 * Turn the key set into the ORDER the transcripts table renders its metadata
 * columns in. TELV1-583.
 *
 * `getUniqueMetadataKeys` builds its Set by walking conversations in whatever
 * order the API returned them, and a Set preserves insertion order. Consuming
 * that directly made the column order "first-seen across the current page of
 * rows", so changing the table sort rearranged the columns — ascending put
 * `environment` fifth, descending pushed it last, because the first row in
 * that direction carried no `environment` at all. Any project whose
 * respondents have differing metadata keys (normal when panel links pass
 * different params) could watch its layout move under it.
 *
 * Alphabetical rather than a curated priority order: metadata keys are raw,
 * per-project, per-panel strings (TELV1-540), so there is no stable set to
 * curate. Ordering by importance belongs in user-reorderable columns, not
 * here.
 *
 * `localeCompare(b, 'en')` is deliberate — the bare form uses the runtime
 * locale, which would order columns differently on a Dutch and an English
 * browser.
 */
export const orderMetadataColumnKeys = (keys: Set<string>): string[] =>
  Array.from(keys).sort((a, b) => a.localeCompare(b, 'en'));

export default getUniqueMetadataKeys;
